import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Table, Button, Space, Image, message, Empty, Spin, Tooltip, Modal, Tag } from 'antd'
import { EditOutlined, PlusOutlined, EyeOutlined, CopyOutlined } from '@ant-design/icons'
import moment from 'moment'
import {
  ExpandedWrapper,
  ExpandedHeader,
  AdsTable,
  CreateAdButton,
  PreviewContent,
  PreviewHeader,
  PreviewBody,
  PreviewFooter,
  CtaButton,
  CtaButtonsContainer
} from './ExpandedRow.styles'
import useAction from 'utils/useAction'
import * as arcAdsActions from 'actions/views/arcAds'
import * as arcCampaignsActions from 'actions/views/arcCampaigns'
import { selectors as arcCampaignsSelectors } from 'domains/arcCampaigns'
import { openArcAdModal, openConfirmModal } from 'reducers/modals/actions'

const getStatusColor = (status, isExpired) => {
  if (isExpired) return 'red'

  switch (status) {
    case 'active': return 'green'
    case 'paused': return 'orange'
    case 'not_opened': return 'default'
    default: return 'default'
  }
}

const ExpandedRow = ({ campaign, includedData }) => {
  const [loading, setLoading] = useState(true)
  const [ads, setAds] = useState([])
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false)
  const [selectedAdForPreview, setSelectedAdForPreview] = useState(null)

  const openArcAdModalFunc = useAction(openArcAdModal)
  const openConfirmModalFunc = useAction(openConfirmModal)
  const cloneAd = useAction(arcAdsActions.cloneArcAd)
  const fetchCampaigns = useAction(arcCampaignsActions.fetchArcCampaigns)
  const page = useSelector(arcCampaignsSelectors.getPage)
  const searchString = useSelector(arcCampaignsSelectors.getSearchString)
  const filters = useSelector(arcCampaignsSelectors.getFilters)
  const sorter = useSelector(arcCampaignsSelectors.getSorter)

  useEffect(() => {
    const loadAds = async () => {
      setLoading(true)
      try {
        const campaignAds = includedData?.filter(item =>
          item.type === 'arc_ads' &&
          item.relationships?.arc_campaign?.data?.id === campaign.id
        ) || []
        setAds(campaignAds)
      } catch (error) {
        message.error('Failed to load ads')
      } finally {
        setLoading(false)
      }
    }

    loadAds()
  }, [campaign.id, includedData])

  const handleCreateAd = () => {
    openArcAdModalFunc({
      campaignId: campaign.id,
      callback: () => {
        fetchCampaigns({ page, search: searchString, filters, sorter })
      }
    })
  }

  const handleEditAd = (ad) => {
    openArcAdModalFunc({
      ad,
      campaignId: campaign.id,
      callback: () => {
        fetchCampaigns({ page, search: searchString, filters, sorter })
      }
    })
  }

  const handleDuplicateAd = (ad) => {
    const callback = () => cloneAd({
      id: ad.id,
      callback: () => {
        fetchCampaigns({ page, search: searchString, filters, sorter })
      }
    })
    const text = `Are you sure you want to clone this ad?`
    openConfirmModalFunc({ callback, text })
  }

  const handlePreviewAd = (ad) => {
    setSelectedAdForPreview(ad)
    setIsPreviewModalVisible(true)
  }

  const handleClosePreview = () => {
    setIsPreviewModalVisible(false)
    setSelectedAdForPreview(null)
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Banner ID',
      dataIndex: ['attributes', 'banner_id'],
      key: 'banner_id',
      width: 100,
      render: (bannerId) => bannerId || '-',
    },
    {
      title: 'Images',
      key: 'images',
      width: 120,
      render: (_, record) => {
        const iconUrl = record.attributes?.icon_url
        const heroUrl = record.attributes?.hero_url

        if (!iconUrl && !heroUrl) return '-'

        return (
          <Space size="small">
            {iconUrl && (
              <Image
                src={iconUrl}
                alt="Icon"
                style={{ width: 30, height: 30, objectFit: 'cover' }}
                preview={true}
              />
            )}
            {heroUrl && (
              <Image
                src={heroUrl}
                alt="Hero"
                style={{ width: 30, height: 30, objectFit: 'cover' }}
                preview={true}
              />
            )}
          </Space>
        )
      },
    },
    {
      title: 'Header',
      dataIndex: ['attributes', 'header'],
      key: 'header',
      width: 200,
      render: (header) => header || '-',
    },
    {
      title: 'Status',
      dataIndex: ['attributes', 'status'],
      key: 'status',
      width: 100,
      render: (status, record) => {
        const isExpired = record.attributes.end_at && moment().isAfter(moment(record.attributes.end_at))
        const displayStatus = isExpired ? 'Expired' : (status || 'N/A')
        return (
          <Tag color={getStatusColor(status, isExpired)}>
            {displayStatus}
          </Tag>
        )
      }
    },
    {
      title: 'Started At',
      dataIndex: ['attributes', 'started_at'],
      key: 'started_at',
      width: 130,
      render: (startedAt) => startedAt ? moment(startedAt).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'End At',
      dataIndex: ['attributes', 'end_at'],
      key: 'end_at',
      width: 130,
      render: (endAt) => endAt ? moment(endAt).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Source',
      dataIndex: ['attributes', 'source'],
      key: 'source',
      width: 100,
      render: (source) => source || '-',
    },
    {
      title: 'Countries',
      dataIndex: ['attributes', 'countries'],
      key: 'countries',
      width: 150,
      render: (countries) => {
        if (!countries || countries.length === 0) return '-'
        return countries.join(', ')
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Preview Ad">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreviewAd(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Ad">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditAd(record)}
            />
          </Tooltip>
          <Tooltip title="Clone Ad">
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleDuplicateAd(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  if (loading) {
    return (
      <ExpandedWrapper>
        <Spin size="large" />
      </ExpandedWrapper>
    )
  }

  return (
    <ExpandedWrapper>
      <ExpandedHeader>
        <h4>Campaign Ads ({ads.length})</h4>
        <CreateAdButton
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateAd}
        >
          Create Ad
        </CreateAdButton>
      </ExpandedHeader>

      {ads.length > 0 ? (
        <AdsTable>
          <Table
            columns={columns}
            dataSource={ads}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </AdsTable>
      ) : (
        <Empty
          description="No ads in this campaign yet"
          style={{ padding: '40px 0' }}
        >
          <Button type="primary" onClick={handleCreateAd}>
            Create First Ad
          </Button>
        </Empty>
      )}

      {/* Preview Modal */}
      <Modal
        title="Ad Preview"
        open={isPreviewModalVisible}
        onCancel={handleClosePreview}
        footer={null}
        width={600}
        centered
      >
        {selectedAdForPreview && (
          <PreviewContent>
            <PreviewHeader>
              {selectedAdForPreview.attributes?.icon_url && (
                <Image
                  src={selectedAdForPreview.attributes.icon_url}
                  alt="Ad icon"
                  style={{ width: '100%', height: 60, objectFit: 'cover', marginBottom: 16 }}
                />
              )}
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  {selectedAdForPreview.attributes?.header || 'No Header'}
                </h3>
                {selectedAdForPreview.attributes?.description && (
                  <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: 14 }}>
                    {selectedAdForPreview.attributes.description}
                  </p>
                )}
              </div>
            </PreviewHeader>

            <PreviewBody>
              {selectedAdForPreview.attributes?.hero_url && (
                <Image
                  src={selectedAdForPreview.attributes.hero_url}
                  alt="Ad hero"
                  style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8 }}
                />
              )}

              <CtaButtonsContainer>
                {selectedAdForPreview.attributes?.cta_text && (
                  <CtaButton type="primary">
                    {selectedAdForPreview.attributes.cta_text}
                  </CtaButton>
                )}
                {selectedAdForPreview.attributes?.secondary_cta_text && (
                  <CtaButton>
                    {selectedAdForPreview.attributes.secondary_cta_text}
                  </CtaButton>
                )}
                {selectedAdForPreview.attributes?.third_cta_text && (
                  <CtaButton>
                    {selectedAdForPreview.attributes.third_cta_text}
                  </CtaButton>
                )}
              </CtaButtonsContainer>
            </PreviewBody>

            {(selectedAdForPreview.attributes?.footer_text || selectedAdForPreview.attributes?.footer_policy_text) && (
              <PreviewFooter>
                {selectedAdForPreview.attributes?.footer_text && (
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                    {selectedAdForPreview.attributes.footer_text}
                  </div>
                )}
                {selectedAdForPreview.attributes?.footer_policy_text && (
                  <div style={{ fontSize: 12, color: '#999' }}>
                    {selectedAdForPreview.attributes.footer_policy_text}
                  </div>
                )}
              </PreviewFooter>
            )}
          </PreviewContent>
        )}
      </Modal>
    </ExpandedWrapper>
  )
}

export default ExpandedRow